/*!
 * kh-motion.js — KHIDYR site-wide motion layer (khidyr.com homepage, Prestige 11.x)
 * ---------------------------------------------------------------------------
 * Modules: REVEALS (IO-gated heading/tile/icon reveals, per-letter clip stagger),
 *          MARQUEE (scroll-velocity skew + native WAAPI playbackRate lerp),
 *          MAGNETIC CTAs (spring integrator, pointer-fine only),
 *          TILE SHEEN (sweep + zoom + label parallax on the kh-pills tiles),
 *          HERO SCRUB (slideshow content translate/fade on scroll).
 * One shared rAF engine: batched reads at frame start, transform/opacity writes
 * only, frame-rate-independent damping, dt clamped, idles (cancels rAF) when
 * nothing is active or document.hidden, every module IO-gated.
 *
 * Single global entry point:  window.KhidyrMotion = { destroy, refresh }
 * Self-injects CSS via <style id="kh-motion-css">. Vanilla JS, no dependencies.
 * prefers-reduced-motion: reduce  ->  boots to a no-op (content untouched).
 * No-JS users never see hidden content (hide classes are added by JS only,
 * immediately before observing).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.KhidyrMotion) return; /* idempotent: never double-boot */

  var STYLE_ID = 'kh-motion-css';
  var EASE = 'cubic-bezier(.16,1,.3,1)'; /* the one signature easing */

  var mediaReduce = null;
  var mediaFine = null;
  try {
    mediaReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaFine = window.matchMedia('(pointer: fine)');
  } catch (e) { /* no matchMedia -> treated as reduced (do nothing) */ }

  /* ---------------------------------------------------- reduced motion gate
     Boot does nothing: no CSS, no classes, no listeners. Content is already
     fully visible because hiding only ever happens via JS below. */
  if (!mediaReduce || mediaReduce.matches) {
    var noop = function () {};
    window.KhidyrMotion = { destroy: noop, refresh: noop };
    return;
  }

  /* ------------------------------------------------------------------ utils */
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  /* frame-rate independent exponential damping */
  function damp(a, b, lambda, dt) { return a + (b - a) * (1 - Math.exp(-lambda * dt)); }
  function supportsClosest(el) { return !!(el && typeof el.closest === 'function'); }

  /* -------------------------------------------------------------------- CSS */
  var CSS = [
    '/* kh-motion (KHIDYR) — injected by kh-motion.js */',
    /* block reveal: translateY + blur -> sharp (signature easing) */
    '.khm-rvb.khm-hidden{opacity:0;transform:translate3d(0,28px,0);filter:blur(8px)}',
    '.khm-rvb.khm-in{opacity:1;transform:translate3d(0,0,0);filter:blur(0);' +
      'transition:opacity .7s ' + EASE + ' var(--khm-d,0ms),' +
      'transform .9s ' + EASE + ' var(--khm-d,0ms),' +
      'filter .9s ' + EASE + ' var(--khm-d,0ms)}',
    /* per-letter clip reveal for big headings */
    '.khm-split .khm-w{display:inline-block;white-space:nowrap}',
    '.khm-split .khm-ch{display:inline-block;overflow:hidden;vertical-align:top;clip-path:inset(0 -0.12em)}',
    '.khm-split .khm-ch-in{display:inline-block}',
    '.khm-split.khm-hidden .khm-ch-in{transform:translate3d(0,118%,0);opacity:0}',
    '.khm-split.khm-in .khm-ch-in{transform:translate3d(0,0,0);opacity:1;' +
      'transition:transform .9s ' + EASE + ',opacity .5s linear;' +
      'transition-delay:calc(var(--khm-d,0ms) + var(--khm-i,0)*30ms)}',
    '.khm-split.khm-hidden{filter:blur(6px)}',
    '.khm-split.khm-in{filter:blur(0);transition:filter .9s ' + EASE + '}',
    /* tile sheen sweep (monochrome white, screen blend) + zoom */
    '.khm-sheen-host{position:relative;overflow:hidden}',
    '.khm-sheen{position:absolute;top:-25%;bottom:-25%;left:0;width:55%;' +
      'background:linear-gradient(105deg,rgba(255,255,255,0) 8%,rgba(255,255,255,.38) 50%,rgba(255,255,255,0) 92%);' +
      'mix-blend-mode:screen;transform:translate3d(-140%,0,0) skewX(-14deg);' +
      'pointer-events:none;z-index:2;will-change:transform}',
    '.khm-sheen.khm-sweep{transform:translate3d(300%,0,0) skewX(-14deg);transition:transform .95s ' + EASE + '}',
    '.khm-tile .kh-pill__img img{transition:transform .8s ' + EASE + ';will-change:transform}',
    '.khm-tile.khm-tile-hover .kh-pill__img img{transform:scale(1.035)}',
    '.khm-tile .kh-pill__label{will-change:transform}',
    /* magnetic CTAs */
    '.khm-mag{will-change:transform}',
    '.khm-btn-in{display:inline-block;pointer-events:none;will-change:transform}',
    /* hero scrub + marquee */
    '.khm-hero-content{will-change:transform,opacity}',
    '.khm-mq{will-change:transform}'
  ].join('\n');

  function injectCSS() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent = CSS;
    (document.head || document.documentElement).appendChild(st);
  }

  /* ============================================================== state ==== */
  var destroyed = false;
  var booted = false;
  var globalOffs = [];           /* global listener removers */
  var ptr = { x: -1e4, y: -1e4, active: false, fine: !!(mediaFine && mediaFine.matches) };
  var vw = window.innerWidth, vh = window.innerHeight;
  var sy = window.scrollY || window.pageYOffset || 0;
  var vel = 0;
  var tier = { parallax: true }; /* quality tier: parallax drops if fps < ~45 */

  function emptyState() {
    return {
      revealIO: null, gateIO: null,
      revealEls: [],   /* els that may still carry khm classes */
      splits: [],      /* { el, html, done } */
      timers: [],
      disposers: [],   /* per-node listener removers */
      hero: null,      /* { sec, els[], top, h, prevTy, prevO } */
      mq: null,        /* { el, sec, skew, rate, anims, ... } */
      magnets: [],     /* { el, inner, x,y,vx,vy, cx,cy,r, was } */
      tiles: [],       /* { el, img, label, sheen, y, cy } */
      gates: { heroEl: null, mqEl: null, pillsEl: null },
      flags: { hero: false, mq: false, pills: false }
    };
  }
  var S = emptyState();

  /* ===================================================== module: reveals === */
  function appendSplitText(parent, text, counter) {
    var chunks = text.split(/(\s+)/);
    for (var c = 0; c < chunks.length; c++) {
      var chunk = chunks[c];
      if (!chunk) continue;
      if (/^\s+$/.test(chunk)) { parent.appendChild(document.createTextNode(' ')); continue; }
      var word = document.createElement('span');
      word.className = 'khm-w';
      for (var k = 0; k < chunk.length; k++) {
        var outer = document.createElement('span');
        outer.className = 'khm-ch';
        var inner = document.createElement('span');
        inner.className = 'khm-ch-in';
        inner.textContent = chunk[k];
        inner.style.setProperty('--khm-i', String(counter.i++));
        outer.appendChild(inner);
        word.appendChild(outer);
      }
      parent.appendChild(word);
    }
  }

  /* Split a heading into per-letter spans. Screen readers read the whole
     phrase via aria-label; the split spans live in one aria-hidden wrapper.
     Only headings whose children are plain text or STRONG/B/EM are split. */
  function trySplitHeading(el) {
    var kids = el.childNodes, i, n;
    var total = 0;
    for (i = 0; i < kids.length; i++) {
      n = kids[i];
      if (n.nodeType === 3) { total += n.nodeValue.length; continue; }
      if (n.nodeType === 1 && /^(STRONG|B|EM)$/.test(n.tagName)) {
        for (var j = 0; j < n.childNodes.length; j++) {
          if (n.childNodes[j].nodeType !== 3) return false;
        }
        total += n.textContent.length;
        continue;
      }
      return false; /* anything else (links, spans, br, icons) -> no split */
    }
    var label = (el.textContent || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
    if (!label || total === 0 || total > 90) return false;

    var original = el.innerHTML;
    var snapshot = [];
    for (i = 0; i < kids.length; i++) snapshot.push(kids[i]);

    var holder = document.createElement('span');
    holder.setAttribute('aria-hidden', 'true');
    var counter = { i: 0 };
    for (i = 0; i < snapshot.length; i++) {
      n = snapshot[i];
      if (n.nodeType === 3) {
        appendSplitText(holder, n.nodeValue, counter);
      } else {
        var wrap = document.createElement(n.tagName.toLowerCase());
        appendSplitText(wrap, n.textContent, counter);
        holder.appendChild(wrap);
      }
    }
    el.setAttribute('aria-label', label);
    el.textContent = '';
    el.appendChild(holder);
    S.splits.push({ el: el, html: original, done: false });
    return true;
  }

  function restoreSplit(entry) {
    if (entry.done) return;
    entry.done = true;
    var el = entry.el;
    el.innerHTML = entry.html;
    el.removeAttribute('aria-label');
    el.classList.remove('khm-split', 'khm-hidden', 'khm-in');
  }

  function stripBlockClasses(el) {
    el.classList.remove('khm-rvb', 'khm-hidden', 'khm-in');
    el.style.removeProperty('--khm-d');
  }

  function scheduleRevealCleanup(el, isSplit, delayMs) {
    var localS = S;
    var t = setTimeout(function () {
      if (localS !== S) return; /* state replaced meanwhile */
      if (isSplit) {
        for (var i = 0; i < S.splits.length; i++) {
          if (S.splits[i].el === el) { restoreSplit(S.splits[i]); break; }
        }
      } else {
        stripBlockClasses(el);
      }
    }, delayMs);
    S.timers.push(t);
  }

  function onRevealEntries(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      var el = entries[i].target;
      var isSplit = el.classList.contains('khm-split');
      el.classList.remove('khm-hidden');
      el.classList.add('khm-in');
      if (S.revealIO) S.revealIO.unobserve(el);
      /* once the transition has fully played, return the node to a pristine,
         class-free state (splits are un-split, aria-label removed) */
      var tail = isSplit ? (1500 + 30 * 90) : 2100;
      scheduleRevealCleanup(el, isSplit, tail);
    }
  }

  function buildReveals(main) {
    var hasIO = typeof IntersectionObserver === 'function';
    var targets = [];
    var i, el;

    /* h2 section headings inside #main, skipping the Prestige slideshow */
    var hs = main.querySelectorAll('h2');
    for (i = 0; i < hs.length; i++) {
      el = hs[i];
      if (supportsClosest(el) && el.closest('.shopify-section--slideshow')) continue;
      if (el.dataset.khm) continue;
      targets.push({ el: el, heading: true, d: 0 });
    }
    /* the kh-pills tiles */
    var pills = main.querySelectorAll('.kh-pill');
    for (i = 0; i < pills.length; i++) {
      if (pills[i].dataset.khm) continue;
      targets.push({ el: pills[i], heading: false, d: i * 80 });
    }
    /* text-with-icons items */
    var icons = main.querySelectorAll('.text-with-icons__item');
    for (i = 0; i < icons.length; i++) {
      if (icons[i].dataset.khm) continue;
      targets.push({ el: icons[i], heading: false, d: i * 90 });
    }
    if (!targets.length) return;
    if (!hasIO) return; /* no IO -> never hide anything */

    S.revealIO = new IntersectionObserver(onRevealEntries, {
      threshold: 0.12,
      rootMargin: '0px 0px -6% 0px'
    });

    for (i = 0; i < targets.length; i++) {
      var t = targets[i];
      el = t.el;
      try {
        el.dataset.khm = '1';
        var split = false;
        if (t.heading) split = trySplitHeading(el);
        if (split) {
          el.classList.add('khm-split');
        } else {
          el.classList.add('khm-rvb');
          if (t.d) el.style.setProperty('--khm-d', t.d + 'ms');
        }
        /* hidden state applied by JS only, immediately before observing */
        el.classList.add('khm-hidden');
        S.revealIO.observe(el);
        S.revealEls.push(el);
      } catch (e) {
        /* never let one bad node break the boot */
        try { el.classList.remove('khm-hidden'); } catch (e2) {}
      }
    }
  }

  /* ===================================================== module: marquee === */
  function buildMarquee(main) {
    var sec = main.querySelector('.shopify-section--scrolling-content');
    if (!sec) return;
    var el = sec.querySelector('marquee-text');
    if (!el) return;
    el.classList.add('khm-mq');
    S.mq = {
      el: el, sec: sec,
      skew: 0, rate: 1,
      anims: null, animTries: 0, nextAnimAt: 0,
      skewWas: false, rateWas: false
    };
  }

  /* Prestige animates marquee-text content with a WAAPI animation; we look it
     up lazily (the custom element may upgrade after us) and only ever touch
     playbackRate, guarded, so the native marquee can never break. */
  function findMarqueeAnims(m, now) {
    if (m.anims && m.anims.length) return;
    if (m.animTries >= 8 || now < m.nextAnimAt) return;
    m.animTries++;
    m.nextAnimAt = now + 1000;
    try {
      if (typeof m.el.getAnimations !== 'function') { m.animTries = 99; return; }
      var all = m.el.getAnimations({ subtree: true });
      var run = [];
      for (var i = 0; i < all.length; i++) {
        try { if (all[i].playState === 'running') run.push(all[i]); } catch (e) {}
      }
      if (run.length) m.anims = run;
    } catch (e) { m.animTries = 99; }
  }

  function marqueeTick(dt, now) {
    var m = S.mq;
    if (!m) return false;
    findMarqueeAnims(m, now);

    var target = S.flags.mq ? clamp(vel * 0.004, -6, 6) : 0; /* skew clamp ±6deg */
    m.skew = damp(m.skew, target, 8, dt);
    var skewBusy = Math.abs(m.skew) > 0.02 || Math.abs(target) > 0.02;
    if (skewBusy || m.skewWas) {
      m.el.style.transform = skewBusy ? 'skewX(' + (-m.skew).toFixed(2) + 'deg)' : '';
    }
    m.skewWas = skewBusy;

    var rateTarget = S.flags.mq ? 1 + clamp(Math.abs(vel) / 2400, 0, 1) * 1.5 : 1; /* 1 -> ~2.5 */
    m.rate = damp(m.rate, rateTarget, 5, dt);
    var rateBusy = Math.abs(m.rate - 1) > 0.01;
    if (m.anims && (rateBusy || m.rateWas)) {
      var r = rateBusy ? m.rate : 1;
      for (var i = 0; i < m.anims.length; i++) {
        try {
          if (m.anims[i].playState === 'running') m.anims[i].playbackRate = r;
        } catch (e) {}
      }
    }
    m.rateWas = rateBusy;
    return skewBusy || rateBusy;
  }

  function resetMarquee(m) {
    if (!m) return;
    try {
      m.el.style.transform = '';
      m.el.classList.remove('khm-mq');
      if (m.anims) {
        for (var i = 0; i < m.anims.length; i++) {
          try { m.anims[i].playbackRate = 1; } catch (e) {}
        }
      }
    } catch (e) {}
  }

  /* ===================================================== module: magnets === */
  function buildMagnets(main) {
    if (!ptr.fine) return; /* never on touch / coarse pointers */
    var btns = main.querySelectorAll('.button');
    for (var i = 0; i < btns.length && i < 14; i++) {
      var b = btns[i];
      if (b.dataset.khmMag) continue;
      b.dataset.khmMag = '1';
      var inner = null;
      /* inner-label parallax only when the button is a single text node */
      if (b.childNodes.length === 1 && b.childNodes[0].nodeType === 3) {
        inner = document.createElement('span');
        inner.className = 'khm-btn-in';
        inner.textContent = b.childNodes[0].nodeValue;
        b.replaceChild(inner, b.childNodes[0]);
      }
      b.classList.add('khm-mag');
      S.magnets.push({
        el: b, inner: inner,
        x: 0, y: 0, vx: 0, vy: 0,
        cx: -1e4, cy: -1e4, r: 90,
        was: false
      });
    }
  }

  function magnetTick(dt) {
    var list = S.magnets;
    if (!list.length || !ptr.fine) return false;
    var busy = false;
    for (var i = 0; i < list.length; i++) {
      var m = list[i];
      var tx = 0, ty = 0;
      if (ptr.active) {
        var dx = ptr.x - m.cx;
        var dy = ptr.y - m.cy;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d < m.r) {
          var f = 1 - d / m.r;            /* 0 at radius edge -> 1 at center */
          tx = clamp(dx * 0.35 * f, -10, 10); /* max offset ~10px */
          ty = clamp(dy * 0.35 * f, -10, 10);
        }
      }
      /* spring-return integrator (critically-damped-ish) */
      m.vx += ((tx - m.x) * 180 - m.vx * 16) * dt;
      m.vy += ((ty - m.y) * 180 - m.vy * 16) * dt;
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      var act = (tx !== 0 || ty !== 0) || Math.abs(m.x) > 0.04 || Math.abs(m.y) > 0.04;
      if (act) {
        m.el.style.transform = 'translate3d(' + m.x.toFixed(2) + 'px,' + m.y.toFixed(2) + 'px,0)';
        if (m.inner) m.inner.style.transform = 'translate3d(' + (m.x * 0.35).toFixed(2) + 'px,' + (m.y * 0.35).toFixed(2) + 'px,0)';
      } else if (m.was) {
        m.el.style.transform = '';
        if (m.inner) m.inner.style.transform = '';
      }
      m.was = act;
      busy = busy || act;
    }
    return busy;
  }

  function restoreMagnet(m) {
    try {
      m.el.style.transform = '';
      m.el.classList.remove('khm-mag');
      delete m.el.dataset.khmMag;
      if (m.inner && m.inner.parentNode === m.el) {
        m.el.replaceChild(document.createTextNode(m.inner.textContent), m.inner);
      }
    } catch (e) {}
  }

  /* ====================================================== module: tiles ==== */
  function buildTiles(main) {
    var pills = main.querySelectorAll('.kh-pill');
    for (var i = 0; i < pills.length; i++) {
      (function (pill) {
        var img = pill.querySelector('.kh-pill__img');
        if (!img) return;
        var label = pill.querySelector('.kh-pill__label');
        pill.classList.add('khm-tile');
        img.classList.add('khm-sheen-host');
        var sheen = document.createElement('div');
        sheen.className = 'khm-sheen';
        sheen.setAttribute('aria-hidden', 'true');
        img.appendChild(sheen);

        var onEnter = function () {
          pill.classList.add('khm-tile-hover');
          sheen.classList.add('khm-sweep');
          wake();
        };
        var onLeave = function () {
          pill.classList.remove('khm-tile-hover');
          sheen.classList.remove('khm-sweep'); /* instant reset off-screen */
        };
        var onSweepEnd = function () { sheen.classList.remove('khm-sweep'); };
        pill.addEventListener('pointerenter', onEnter, { passive: true });
        pill.addEventListener('pointerleave', onLeave, { passive: true });
        sheen.addEventListener('transitionend', onSweepEnd, { passive: true });
        S.disposers.push(function () {
          pill.removeEventListener('pointerenter', onEnter);
          pill.removeEventListener('pointerleave', onLeave);
          sheen.removeEventListener('transitionend', onSweepEnd);
        });
        S.tiles.push({ el: pill, img: img, label: label, sheen: sheen, y: 0, cy: 0 });
      })(pills[i]);
    }
  }

  /* label parallax: tiny counter-translate on the label vs the static image */
  function tileTick() {
    if (!S.flags.pills || !tier.parallax) return false;
    var busy = false;
    for (var i = 0; i < S.tiles.length; i++) {
      var t = S.tiles[i];
      if (!t.label) continue;
      var rel = (t.cy - vh / 2) / Math.max(1, vh / 2); /* -1 top .. 1 bottom */
      var y = clamp(rel * -7, -8, 8);
      if (Math.abs(y - t.y) > 0.25) {
        t.y = y;
        t.label.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
        busy = true;
      }
    }
    return busy;
  }

  function restoreTile(t) {
    try {
      t.el.classList.remove('khm-tile', 'khm-tile-hover');
      t.img.classList.remove('khm-sheen-host');
      if (t.sheen && t.sheen.parentNode) t.sheen.parentNode.removeChild(t.sheen);
      if (t.label) t.label.style.transform = '';
    } catch (e) {}
  }

  /* ======================================================= module: hero ==== */
  function buildHero(main) {
    var sec = main.querySelector('.shopify-section--slideshow');
    if (!sec) return;
    var nodes = sec.querySelectorAll('.slideshow__slide-content');
    if (!nodes.length) return; /* expected inner nodes missing -> skip silently */
    var els = [];
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add('khm-hero-content');
      els.push(nodes[i]);
    }
    S.hero = { sec: sec, els: els, top: 0, h: 1, prevTy: 1e9, prevO: 1e9 };
  }

  function heroTick() {
    var h = S.hero;
    if (!h || !S.flags.hero) return false;
    var dy = sy - h.top;
    if (dy < 0) dy = 0;
    var ty = -dy * 0.12; /* content drifts up at ~0.12x scroll delta */
    var o = 1 - clamp(dy / Math.max(1, h.h * 0.8), 0, 1);
    if (Math.abs(ty - h.prevTy) < 0.3 && Math.abs(o - h.prevO) < 0.004) return false;
    h.prevTy = ty;
    h.prevO = o;
    for (var i = 0; i < h.els.length; i++) {
      var el = h.els[i];
      el.style.transform = ty !== 0 ? 'translate3d(0,' + ty.toFixed(1) + 'px,0)' : '';
      el.style.opacity = o >= 0.999 ? '' : o.toFixed(3);
    }
    return true;
  }

  function restoreHero(h) {
    if (!h) return;
    for (var i = 0; i < h.els.length; i++) {
      try {
        h.els[i].style.transform = '';
        h.els[i].style.opacity = '';
        h.els[i].classList.remove('khm-hero-content');
      } catch (e) {}
    }
  }

  /* ===================================================== IO section gates == */
  function buildGates() {
    var hasIO = typeof IntersectionObserver === 'function';
    S.gates.heroEl = S.hero ? S.hero.sec : null;
    S.gates.mqEl = S.mq ? S.mq.sec : null;
    S.gates.pillsEl = null;
    if (S.tiles.length) {
      var t0 = S.tiles[0].el;
      S.gates.pillsEl = (supportsClosest(t0) && (t0.closest('.kh-pills-wrap') || t0.closest('.shopify-section'))) || t0.parentNode || t0;
    }
    if (!hasIO) { S.flags.hero = S.flags.mq = S.flags.pills = true; return; }
    S.gateIO = new IntersectionObserver(function (es) {
      for (var i = 0; i < es.length; i++) {
        var tgt = es[i].target;
        var on = es[i].isIntersecting;
        if (tgt === S.gates.heroEl) S.flags.hero = on;
        else if (tgt === S.gates.mqEl) S.flags.mq = on;
        else if (tgt === S.gates.pillsEl) S.flags.pills = on;
      }
      wake();
    }, { rootMargin: '12% 0px 12% 0px' });
    if (S.gates.heroEl) S.gateIO.observe(S.gates.heroEl);
    if (S.gates.mqEl) S.gateIO.observe(S.gates.mqEl);
    if (S.gates.pillsEl) S.gateIO.observe(S.gates.pillsEl);
  }

  /* ============================================================== engine === */
  var raf = 0, running = false, last = 0, idleFrames = 0;
  var fpsAcc = 0, fpsN = 0, warmed = 0;

  function frame(now) {
    raf = requestAnimationFrame(frame);
    var dt = (now - last) / 1000;
    last = now;
    if (!(dt > 0)) dt = 0.016;
    if (dt > 0.05) dt = 0.05; /* dt clamp */

    /* quality tier: after warm-up, sustained <45fps drops parallax work */
    warmed++;
    if (warmed > 90 && tier.parallax) {
      fpsAcc += dt; fpsN++;
      if (fpsN >= 90) {
        if (fpsN / fpsAcc < 45) tier.parallax = false;
        fpsAcc = 0; fpsN = 0;
      }
    }

    /* ---- READS (batched at frame start; transforms never dirty layout) ---- */
    var ny = window.scrollY || window.pageYOffset || 0;
    var rawV = clamp((ny - sy) / dt, -6000, 6000);
    var scrolled = ny !== sy;
    sy = ny;
    vel = damp(vel, rawV, 6, dt);
    if (!scrolled && Math.abs(vel) < 0.5) vel = 0; /* settle exactly to rest */

    var i, r;
    if (ptr.fine && ptr.active && S.magnets.length) {
      for (i = 0; i < S.magnets.length; i++) {
        var m = S.magnets[i];
        r = m.el.getBoundingClientRect();
        /* subtract our own transform so the center is stable */
        m.cx = r.left + r.width / 2 - m.x;
        m.cy = r.top + r.height / 2 - m.y;
        m.r = 90 + Math.max(r.width, r.height) / 2; /* ~90px attraction shell */
      }
    }
    if (S.flags.pills && tier.parallax && S.tiles.length) {
      for (i = 0; i < S.tiles.length; i++) {
        r = S.tiles[i].el.getBoundingClientRect();
        S.tiles[i].cy = r.top + r.height / 2;
      }
    }

    /* ---- WRITES (transform/opacity only) ---- */
    var busy = false;
    if (heroTick()) busy = true;
    if (marqueeTick(dt, now)) busy = true;
    if (magnetTick(dt)) busy = true;
    if (tileTick()) busy = true;
    if (scrolled || vel !== 0) busy = true;

    if (busy) {
      idleFrames = 0;
    } else if (++idleFrames > 30) {
      stop(); /* fully idle: cancel rAF until something wakes us */
    }
  }

  function start() {
    if (running || destroyed || document.hidden) return;
    running = true;
    idleFrames = 0;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (!running) return;
    running = false;
    cancelAnimationFrame(raf);
  }
  function wake() {
    if (destroyed) return;
    if (!running) start(); else idleFrames = 0;
  }

  /* ======================================================== measurement === */
  function measure() {
    vw = window.innerWidth;
    vh = window.innerHeight;
    var s = window.scrollY || window.pageYOffset || 0;
    if (S.hero) {
      try {
        var r = S.hero.sec.getBoundingClientRect();
        S.hero.top = r.top + s;
        S.hero.h = Math.max(1, r.height);
        S.hero.prevTy = 1e9; /* force one re-write */
        S.hero.prevO = 1e9;
      } catch (e) {}
    }
  }

  /* ==================================================== global listeners == */
  function on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    globalOffs.push(function () { target.removeEventListener(type, fn, opts); });
  }

  function attachGlobalListeners() {
    on(window, 'scroll', wake, { passive: true });

    if (ptr.fine) {
      on(window, 'pointermove', function (e) {
        ptr.x = e.clientX;
        ptr.y = e.clientY;
        ptr.active = true;
        wake();
      }, { passive: true });
      on(document, 'pointerout', function (e) {
        if (!e.relatedTarget) ptr.active = false;
      }, { passive: true });
    }

    var rzT = 0;
    on(window, 'resize', function () {
      clearTimeout(rzT);
      rzT = setTimeout(function () { measure(); wake(); }, 150);
    }, { passive: true });
    globalOffs.push(function () { clearTimeout(rzT); });

    on(window, 'load', function () { measure(); wake(); }, { once: true });

    on(document, 'visibilitychange', function () {
      if (document.hidden) stop(); else wake();
    });

    /* theme-editor compatibility: re-scan when sections are (re)rendered */
    var rfT = 0;
    var scheduleRefresh = function () {
      clearTimeout(rfT);
      rfT = setTimeout(function () { api.refresh(); }, 60);
    };
    on(document, 'shopify:section:load', scheduleRefresh);
    on(document, 'shopify:section:unload', scheduleRefresh);
    globalOffs.push(function () { clearTimeout(rfT); });

    /* if the user enables reduced motion while we run, get out of the way */
    var onReduceChange = function (e) { if (e.matches) api.destroy(); };
    if (mediaReduce.addEventListener) {
      mediaReduce.addEventListener('change', onReduceChange);
      globalOffs.push(function () { mediaReduce.removeEventListener('change', onReduceChange); });
    } else if (mediaReduce.addListener) {
      mediaReduce.addListener(onReduceChange);
      globalOffs.push(function () { mediaReduce.removeListener(onReduceChange); });
    }
  }

  /* ============================================================ lifecycle == */
  function buildModules() {
    S = emptyState();
    var main = document.getElementById('main');
    if (!main) return; /* nothing to enhance; engine stays idle */
    /* each module is independently guarded: one bad section never throws */
    try { buildReveals(main); } catch (e) {}
    try { buildMarquee(main); } catch (e) {}
    try { buildMagnets(main); } catch (e) {}
    try { buildTiles(main); } catch (e) {}
    try { buildHero(main); } catch (e) {}
    try { buildGates(); } catch (e) {}
  }

  /* full=true (destroy): restore everything including data flags */
  function dispose(state, full) {
    var i;
    try { if (state.revealIO) state.revealIO.disconnect(); } catch (e) {}
    try { if (state.gateIO) state.gateIO.disconnect(); } catch (e) {}
    for (i = 0; i < state.timers.length; i++) clearTimeout(state.timers[i]);
    for (i = 0; i < state.disposers.length; i++) {
      try { state.disposers[i](); } catch (e) {}
    }
    for (i = 0; i < state.splits.length; i++) {
      try {
        if (full) {
          restoreSplit(state.splits[i]);
        } else if (state.splits[i].el.classList.contains('khm-hidden')) {
          /* never leave content hidden behind */
          restoreSplit(state.splits[i]);
          delete state.splits[i].el.dataset.khm;
        }
      } catch (e) {}
    }
    for (i = 0; i < state.revealEls.length; i++) {
      var el = state.revealEls[i];
      try {
        if (el.classList.contains('khm-hidden')) {
          stripBlockClasses(el);
          delete el.dataset.khm;
        } else if (full) {
          stripBlockClasses(el);
          delete el.dataset.khm;
        }
      } catch (e) {}
    }
    for (i = 0; i < state.magnets.length; i++) restoreMagnet(state.magnets[i]);
    for (i = 0; i < state.tiles.length; i++) restoreTile(state.tiles[i]);
    restoreHero(state.hero);
    resetMarquee(state.mq);
  }

  var api = {
    refresh: function () {
      if (destroyed || !booted) return;
      try {
        dispose(S, false);
        buildModules();
        measure();
        wake();
      } catch (e) {}
    },
    destroy: function () {
      if (destroyed) return;
      destroyed = true;
      stop();
      try { dispose(S, true); } catch (e) {}
      S = emptyState();
      for (var i = 0; i < globalOffs.length; i++) {
        try { globalOffs[i](); } catch (e) {}
      }
      globalOffs.length = 0;
      var st = document.getElementById(STYLE_ID);
      if (st && st.parentNode) st.parentNode.removeChild(st);
    }
  };

  function boot() {
    if (destroyed || booted) return;
    booted = true;
    try {
      injectCSS();
      attachGlobalListeners();
      buildModules();
      measure();
      wake();
    } catch (e) {
      /* a failed boot must never break the storefront */
      try { api.destroy(); } catch (e2) {}
    }
  }

  window.KhidyrMotion = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
